import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { FormattedMessage } from 'react-intl';
import { useModelService } from 'services/model/model.service';
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from 'hooks/state/useAppStore';
import { DataEntry, DataResult } from 'models/data';
import { PerformanceGapDisplay } from 'pages/Benchmarking/common/performance-gap/PerformanceGapDisplay';
import { Interpretation } from 'components/MeasureComparison';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { ExpansionOpportunities } from 'pages/Benchmarking/common/expansion-opportunities/ExpansionOpportunities';
import { useBenchmarkDataResult } from 'pages/Benchmarking/queryHooks';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { MarketplaceCountriesDrilldownInfoContent } from 'pages/Benchmarking/drilldown/MarketplaceCountriesDrilldownInfoContent';

export interface BenchmarkingMarketplaceDrillDownProps {
  marketplace: string;
}

interface DashboardData {
  data: DataResult;
}

interface ExpansionOpportunitiesData {
  data: DataResult;
  benchmark: string;
}

export function BenchmarkingMarketplaceDrillDown({ marketplace }: BenchmarkingMarketplaceDrillDownProps) {
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const marketplaceDrillDownInfo = {
      titleKey: 'benchmarking.marketplaces_tab.drilldown.info.title',
      content: <MarketplaceCountriesDrilldownInfoContent />,
    };

    setContent(marketplaceDrillDownInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const { getMeasures, getAttribute } = useModelService();

  const { breadcrumbs, setBreadcrumbs } = useBreadcrumbStore();

  useEffect(() => {
    if (breadcrumbs) {
      const drilldownIndex = breadcrumbs.findIndex(bc => bc.path === 'drilldown');

      if (drilldownIndex >= 0) {
        const bcSlice = breadcrumbs.slice(0, drilldownIndex);
        bcSlice.push({ path: 'marketplaces' });
        bcSlice.push({ path: marketplace });
        setBreadcrumbs(bcSlice);
      }
    }
  }, [marketplace, breadcrumbs]);

  const chnCountry = getAttribute('chnCountry');
  const [ordGmvVal, retQtyRateOD, cncQtyRate] = getMeasures(['ordGmvVal', 'retQtyRateExpOD', 'cncQtyRate']);

  const [dashboardData, setDashboardData] = useState<DashboardData>();

  const [expansionOpportunitiesData, setExpansionOpportunitiesData] = useState<ExpansionOpportunitiesData>();
  const { countries, benchmark } = useAppStore();

  const { allCountries } = useTransitiveCacheStore();

  const selectedCountryCodes = useMemo(() => (countries ?? []).map(c => c.toString()), [countries]);

  const allCountryCodes = useMemo(() => (allCountries ?? []).map(c => c.countryCode.toString()), [allCountries]);

  const { data: inflightDrilldownData, isPending } = useBenchmarkDataResult(`drilldown/marketplace/${marketplace}`);

  useEffect(() => {
    if (inflightDrilldownData) {
      const filteredMarketplaceCountries: DataEntry[] = [];
      const expansionOpportunities: DataEntry[] = [];

      inflightDrilldownData.sets.marketplaceCountries
        .filter(mc => mc.benchmarks && mc.benchmarks[benchmark] !== null)
        .filter(mc => marketplace === mc.attributes.chnMarketplace)
        .forEach(marketplaceCountry => {
          const selectedCountryFound =
            selectedCountryCodes.length > 0
              ? selectedCountryCodes.includes(marketplaceCountry.attributes.chnCountry)
              : allCountryCodes.includes(marketplaceCountry.attributes.chnCountry);

          const hasOrders =
            ordGmvVal.getValue(marketplaceCountry.values) > 0 || ordGmvVal.getValue(marketplaceCountry.compareValues);

          if (!selectedCountryFound || !hasOrders) {
            expansionOpportunities.push(marketplaceCountry);
          } else {
            filteredMarketplaceCountries.push(marketplaceCountry);
          }
        });

      setDashboardData({
        ...inflightDrilldownData,
        data: {
          ...inflightDrilldownData,
          sets: {
            ...inflightDrilldownData?.sets,
            marketplaceCountries: filteredMarketplaceCountries,
          },
        },
      });

      setExpansionOpportunitiesData({
        ...inflightDrilldownData,
        benchmark: benchmark,
        data: {
          ...inflightDrilldownData,
          sets: {
            ...inflightDrilldownData?.sets,
            marketplaceCountries: expansionOpportunities,
          },
        },
      });
    }
  }, [inflightDrilldownData, selectedCountryCodes, allCountries, marketplace, benchmark]);

  const isLoading = isPending || !dashboardData || !allCountries || !expansionOpportunitiesData;

  const dataSet = dashboardData?.data.sets.marketplaceCountries ?? [];

  return (
    <PageContainer>
      <PageSectionTitle>{marketplace}</PageSectionTitle>
      <PerformanceGapDisplay
        dataSet={dataSet}
        improvementMeasure={ordGmvVal}
        groupingAttribute={chnCountry}
        interpretation={Interpretation.POSITIVE}
        benchmark={expansionOpportunitiesData?.benchmark ?? ''}
        isPending={isLoading}
      />

      <ExpansionOpportunities
        expansionOpportunities={expansionOpportunitiesData?.data.sets.marketplaceCountries ?? []}
        benchmark={expansionOpportunitiesData?.benchmark ?? ''}
        isPending={isLoading}
      />
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.returns-canellation" />
      </PageSectionTitle>
      <Tile>
        <PerformanceGapDisplay
          dataSet={dataSet}
          improvementMeasure={retQtyRateOD}
          groupingAttribute={chnCountry}
          interpretation={Interpretation.NEGATIVE}
          benchmark={expansionOpportunitiesData?.benchmark ?? ''}
          isPending={isLoading}
        />
      </Tile>
      <Tile>
        <PerformanceGapDisplay
          dataSet={dataSet}
          improvementMeasure={cncQtyRate}
          groupingAttribute={chnCountry}
          interpretation={Interpretation.NEGATIVE}
          benchmark={expansionOpportunitiesData?.benchmark ?? ''}
          isPending={isLoading}
        />
      </Tile>
    </PageContainer>
  );
}
